/* eslint-disable max-lines-per-function */
import { useCalendarContext } from '@/domain/termine/features/Calendar/shared/CalendarContext';
import { ListWeekHeader } from '@/domain/termine/features/Calendar/List/ListWeekHeader';
import { generateWiederholungen } from '@/domain/termine/shared/utils/generateWiederholungen';
import { Day } from '@/domain/termine/type';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { JSX, useCallback, useMemo } from 'react';
import { ListDay } from '@/domain/termine/features/Calendar/List/ListDay';

interface ListWeekProps {
  week: Day[];
}

export function ListWeek({ week }: ListWeekProps): JSX.Element {
  const { termine, standortId, startDate, endDate, monthDate, feiertage } =
    useCalendarContext();
  const { t } = useTranslation();

  const alleTermine = useMemo(
    () =>
      (standortId
        ? termine.filter((t) => t.idStandort === standortId)
        : termine
      ).flatMap((termin) => generateWiederholungen(termin, startDate, endDate)),
    [standortId, termine, startDate, endDate],
  );

  const termineInWeek = useMemo(
    () =>
      alleTermine.filter((t) => {
        const terminDate = dayjs(t.datum).locale('de').startOf('week');
        const weekStart = week[0].locale('de').startOf('week').startOf('day');
        const weekEnd = week[0].locale('de').endOf('week').startOf('day');
        return (
          (terminDate.isAfter(weekStart) || terminDate.isSame(weekStart)) &&
          (terminDate.isBefore(weekEnd) || terminDate.isSame(weekEnd))
        );
      }),
    [alleTermine, week],
  );

  const daysInSelectedMonth = useMemo(
    () => week.filter((day) => day.month() === dayjs(monthDate.month).month()),
    [week, monthDate],
  );
  const weekHasFeiertag = daysInSelectedMonth.some((day: Day) =>
    feiertage.some((f) => day.format('YYYY-MM-DD') === f.datum),
  );

  const getTermineForDay = useCallback(
    (day: Day) =>
      termineInWeek.filter((t) => day.isSame(dayjs(t.datum), 'day')),
    [termineInWeek],
  );

  const weekHasTermine = useMemo(
    () => daysInSelectedMonth.some((day) => getTermineForDay(day).length > 0),
    [daysInSelectedMonth, getTermineForDay],
  );
  const hasFeiertag = (day: Day) =>
    feiertage.some((f) => day.format('YYYY-MM-DD') === f.datum);

  const showNoTermine = !weekHasFeiertag && !weekHasTermine;

  function renderNoTermine() {
    return <span>{t('domain.termine.keineTermine')}</span>;
  }

  function renderDays() {
    return daysInSelectedMonth.map((day) => (
      <ListDay
        key={day.format('DD-MM-YYYY')}
        day={day}
        week={week}
        getTermineForDay={(d) => getTermineForDay(d)}
        hasFeiertag={hasFeiertag}
        alleTermine={alleTermine}
      />
    ));
  }
  return (
    <div className="rounded-xl">
      <ListWeekHeader week={week} />
      {showNoTermine ? renderNoTermine() : renderDays()}
    </div>
  );
}
